import { useState, createContext, useEffect } from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  isRouteErrorResponse,
  useRouteError,
  useLocation,
  useNavigation,
} from "@remix-run/react";
import type { LinksFunction, ShouldRevalidateFunctionArgs } from "@remix-run/node";
import Header from "./ui-apps/design-system/organisms/Header";
import Footer from "./ui-apps/design-system/organisms/Footer";
import styles from "./tailwind.css?url";
import SignedOutSideNavbar from "./ui-apps/design-system/organisms/SignedOutSideNavbar";
import SignedInSideNavbar from "./ui-apps/design-system/organisms/SignedInSideNavbar";
import { UserContext } from "./utilities/userContext";
import { securedRouteLoader } from "./utilities/secured-route.server";
import MobileSignedInHeader from "./ui-apps/design-system/organisms/MobileSignedInHeader";
import MobileSignedOutHeader from "./ui-apps/design-system/organisms/MobileSignedOutHeader";
import OnboardingModal from "./ui-apps/onboarding-app/OnboardingModal";
import TabContentSpinner from "./ui-apps/design-system/atoms/TabContentSpinner";
import { MOBILE_HEADER_SCROLL_TRANSITION_CLASS } from "./ui-apps/design-system/constants/mobileSignedInHeader";
import {
  resetWindowMobileNavScroll,
  setMobileHeaderReservesSpace,
  useMobileTickerSiteHeaderVisible,
} from "./ui-apps/hooks/useMobileNavScrollVisibility";
import { isFinancialGlanceTickerPath } from "./utilities/tickerRoutePaths.util";

export { securedRouteLoader as loader };

/** Query-string updates on /screener are client-driven; skip root revalidation (avoids full-page spinner). */
export function shouldRevalidate({
  currentUrl,
  nextUrl,
  formMethod,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) {
  if (
    !formMethod &&
    currentUrl.pathname === "/screener" &&
    nextUrl.pathname === "/screener"
  ) {
    return false;
  }
  return defaultShouldRevalidate;
}

const ONBOARDING_EXCLUDED_PATHS = [
  "/sign-in",
  "/sign-up",
  "/email-sign-in",
  "/email-sign-up",
  "/auth-redirect",
  "/google-sign-up",
  "/settings",
];

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const loaderData = useLoaderData<typeof securedRouteLoader>();
  const {
    isAuthenticated,
    user,
    displayPricingModal,
    isBeta,
    hasCompletedOnboarding,
    onboardingStep,
    onboardingPreferences,
  } = loaderData;
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const location = useLocation();
  const navigation = useNavigation();
  const isRouteLoading = navigation.state === 'loading';
  const isDashboardRoute =
    location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/');
  const showFullPageSpinner = isRouteLoading && !isDashboardRoute;
  const isRootPath = location.pathname === "/";
  const isAuthPath = location.pathname === "/sign-in" || location.pathname === "/sign-up";
  const isAboutPath = location.pathname === "/about";
  const isUnsubWeeklyNewsPath = location.pathname.startsWith("/unsub-weekly-news");
  const isHomePath =
    location.pathname === "/home" || location.pathname.startsWith("/home/");
  const isTickerDetailPath = isFinancialGlanceTickerPath(location.pathname);
  const isTickerSiteHeaderVisible = useMobileTickerSiteHeaderVisible();

  useEffect(() => {
    setMobileHeaderReservesSpace(!isTickerDetailPath);
    resetWindowMobileNavScroll();
  }, [isTickerDetailPath, location.pathname]);

  const isOnboardingExcludedPath = ONBOARDING_EXCLUDED_PATHS.some((p) =>
    location.pathname === p || location.pathname.startsWith(`${p}/`),
  );
  const showOnboardingModal =
    isAuthenticated &&
    !hasCompletedOnboarding &&
    !onboardingDismissed &&
    !isOnboardingExcludedPath &&
    !isUnsubWeeklyNewsPath;

  const showSidebar =
    !isAboutPath &&
    !isUnsubWeeklyNewsPath &&
    (isAuthenticated || (!isAuthenticated && !isRootPath && !isAuthPath));

  return (
    <html
      lang="en"
      className={`h-full bg-background ${
        isTickerDetailPath ? "overflow-hidden" : ""
      }`}
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
        <Links />
        <Meta />
      </head>
      <body
        className={`text-black overflow-x-hidden [scrollbar-gutter:stable] bg-background ${
          isTickerDetailPath
            ? "h-[100dvh] max-h-[100dvh] overflow-hidden"
            : "min-h-full"
        }`}
      >
        <UserContext.Provider value={{ isAuthenticated, user }}>
          <div
            className={`flex min-w-0 bg-background ${
              isTickerDetailPath
                ? "h-full min-h-0 overflow-hidden"
                : "min-h-screen"
            }`}
          >
            {showSidebar && (
              isAuthenticated ? (
                <SignedInSideNavbar />
              ) : (
                <SignedOutSideNavbar isAuthenticated={isAuthenticated} />
              )
            )}

            {/* Home omits overflow clipping so the feed tabs can stick to the window on desktop and mobile. */}
            <div
              className={`flex min-w-0 flex-1 flex-col bg-background ${showSidebar ? "lg:ml-[250px]" : ""} ${
                isTickerDetailPath
                  ? "h-full min-h-0 overflow-hidden"
                  : isHomePath
                    ? ""
                    : "overflow-x-clip"
              }`}
            >

              {(isRootPath || isAuthPath || (isAboutPath && !isAuthenticated)) && (
                <Header
                  setMobileMenuOpen={setMobileMenuOpen}
                  mobileMenuOpen={mobileMenuOpen}
                  isAuthenticated={isAuthenticated}
                />
              )}

              {/* Mobile header. Ticker detail scrolls inside the page, so the bar stays in this column instead of overlaying the glance.
                  On ticker, collapse the bar when the glance scrolls past the top so SectionNavPills slide up into its place. */}
              <div
                id="mobile-header"
                className={
                  isUnsubWeeklyNewsPath
                    ? "hidden"
                    : isTickerDetailPath
                      ? `relative z-40 grid shrink-0 transition-[grid-template-rows] ${MOBILE_HEADER_SCROLL_TRANSITION_CLASS} lg:hidden ${
                          isTickerSiteHeaderVisible
                            ? "grid-rows-[1fr]"
                            : "grid-rows-[0fr]"
                        }`
                      : "lg:hidden"
                }
                aria-hidden={
                  isTickerDetailPath && !isTickerSiteHeaderVisible
                    ? true
                    : undefined
                }
              >
                <div
                  className={
                    isTickerDetailPath
                      ? `min-h-0 overflow-hidden transition-transform ${MOBILE_HEADER_SCROLL_TRANSITION_CLASS} ${
                          isTickerSiteHeaderVisible
                            ? "translate-y-0"
                            : "-translate-y-full"
                        }`
                      : undefined
                  }
                >
                  {isAuthenticated ? (
                    <MobileSignedInHeader />
                  ) : (
                    <MobileSignedOutHeader
                      setMobileMenuOpen={setMobileMenuOpen}
                      mobileMenuOpen={mobileMenuOpen}
                    />
                  )}
                </div>
              </div>

              <main
                className={`flex-grow bg-background ${
                  isTickerDetailPath
                    ? "mb-0 flex h-0 min-h-0 flex-1 flex-col overflow-hidden"
                    : isHomePath
                      ? "isolate mb-0"
                      : isRootPath
                        ? "mb-0"
                        : "mb-10"
                }`}
              >
                {showFullPageSpinner ? (
                  <TabContentSpinner className="min-h-[60vh]" />
                ) : (
                  <Outlet
                    context={{
                      auth: {
                        isAuthenticated,
                        user,
                        displayPricingModal,
                        hasCompletedOnboarding,
                        isBeta,
                      },
                      onboardingPreferences: onboardingPreferences ?? {},
                    }}
                  />
                )}
              </main>
              {showOnboardingModal && (
                <OnboardingModal
                  user={user}
                  isOpen={showOnboardingModal}
                  initialStep={onboardingStep ?? 1}
                  initialPreferences={onboardingPreferences ?? {}}
                  onComplete={() => setOnboardingDismissed(true)}
                />
              )}
              <Footer isAuthenticated={isAuthenticated} />
            </div>
          </div>
        </UserContext.Provider>
        <Scripts />
        <ScrollRestoration />
      </body>
    </html>
  );
}

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: styles },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=IBM+Plex+Mono:wght@500;600&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Outfit:wght@400;500;600&display=swap",
  },
];

export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <html lang="en" className="h-full bg-background">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>{`${error.status} ${error.statusText}`}</title>
          <Links />
        </head>
        <body className="bg-background min-h-full">
          <div className="flex min-h-screen items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {error.status} {error.statusText}
              </h1>
              <p className="text-gray-600 mb-8">
                {error.data?.message || "Something went wrong"}
              </p>
              <a
                href="/"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800"
              >
                Go back home
              </a>
            </div>
          </div>
          <Scripts />
        </body>
      </html>
    );
  }

  return (
    <html lang="en" className="h-full bg-background">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Unexpected Error</title>
        <Links />
      </head>
      <body className="bg-background min-h-full">
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Unexpected Error
            </h1>
            <p className="text-gray-600 mb-8">
              An unexpected error occurred. Please try again later.
            </p>
            <a
              href="/"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800"
            >
              Go back home
            </a>
          </div>
        </div>
        <Scripts />
      </body>
    </html>
  );
}

function Document() {
  return <App />;
}

export default Document;